import { Field } from '@ontruck/orc';
import { Button, Select } from 'refactored/ui/form';
import { useSignUpForm } from 'refactored/features/signUpForm';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import styled from 'styled-components';
import { gridSizes, fontSizes, weights } from 'utils/style';
import routes from 'utils/routes';
import { Link as InternalLink } from 'react-router-dom';
import { unflatWorkingLocations } from 'refactored/features/signUpForm';
import api from 'api';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';

const LandingRegisterSchema = Yup.object().shape({
  company_name: Yup.string().required('company_name.required'),
  cif: Yup.string().required('cif.required'),
  full_name: Yup.string().required('full_name.required'),
  email: Yup.string().email('email.invalid').required('email.required'),
  phone: Yup.string().required('contact_phone.required'),
  working_location: Yup.string().required('working_location.required'),
});

const LandingRegisterForm = ({ className, props }) => {
  const { t } = useTranslation('SignUp');

  const [requestForAccess, setRequestForAccess] = useState(false);
  const [error, setError] = useState(false);

  const { workingLocations } = useSignUpForm();

  const handleSubmit = async (values) => {
    try {
      if (values.working_location && values.working_location !== '') {
        for (const unflatWorkingLocation in unflatWorkingLocations) {
          if (
            unflatWorkingLocations[unflatWorkingLocation].includes(
              values.working_location
            )
          ) {
            values.working_location = unflatWorkingLocation;
          }
        }
      }

      await api.requestForAccess(values);
      setRequestForAccess(true);
    } catch (err) {
      console.error(err);
      setError(true);
    }
  };

  return (
    <div className={className}>
      {requestForAccess && (
        <>
          <h2 className="title">{t('SignUpForm.success_title')}</h2>
          <p>{t(`SignUpForm.success_message`)}</p>
          <InternalLink to={routes.login}>
            {' '}
            {t('SignUpForm.success_back_login')}{' '}
          </InternalLink>
        </>
      )}
      {!requestForAccess && (
        <>
          <h2 className="title">{t('SignUpForm.title')}</h2>
          <Formik
            initialValues={{
              company_name: '',
              cif: '',
              full_name: '',
              email: '',
              phone: '',
              working_location: '',
            }}
            validationSchema={LandingRegisterSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, handleChange }) => {
              return (
                <Form className="form">
                  <div
                    className={`${
                      touched.working_location &&
                      errors.working_location &&
                      'error-input'
                    }`}
                  >
                    <Select
                      id="working_location"
                      placeholder={t('SignUpForm.working_location_label')}
                      name="working_location"
                      onChange={handleChange}
                      required
                      options={workingLocations}
                    />
                    {touched.working_location && errors.working_location ? (
                      <div className="error-field">
                        {t(
                          `SignUpForm.form_error_messages.${errors.working_location}`
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={`${
                      touched.full_name && errors.full_name && 'error-input'
                    }`}
                  >
                    <Field
                      id="full_name"
                      label={t('SignUpForm.full_name_label')}
                      name="full_name"
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                    {touched.full_name && errors.full_name ? (
                      <div className="error-field">
                        {t(
                          `SignUpForm.form_error_messages.${errors.full_name}`
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={`${
                      touched.email && errors.email && 'error-input'
                    }`}
                  >
                    <Field
                      id="email"
                      label={t('SignUpForm.email_label')}
                      name="email"
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                    {touched.email && errors.email ? (
                      <div className="error-field">
                        {t(`SignUpForm.form_error_messages.${errors.email}`)}
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={`${
                      touched.phone && errors.phone && 'error-input'
                    }`}
                  >
                    <Field
                      id="phone"
                      label={t('SignUpForm.contact_phone_label')}
                      name="phone"
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                    {touched.phone && errors.phone ? (
                      <div className="error-field">
                        {t(`SignUpForm.form_error_messages.${errors.phone}`)}
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={`${
                      touched.company_name &&
                      errors.company_name &&
                      'error-input'
                    }`}
                  >
                    <Field
                      id="company_name"
                      label={t('SignUpForm.company_name_label')}
                      name="company_name"
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                    {touched.company_name && errors.company_name ? (
                      <div className="error-field">
                        {t(
                          `SignUpForm.form_error_messages.${errors.company_name}`
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div
                    className={`${touched.cif && errors.cif && 'error-input'}`}
                  >
                    <Field
                      id="cif"
                      label={t('SignUpForm.cif_label')}
                      name="cif"
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                    {touched.cif && errors.cif ? (
                      <div className="error-field">
                        {t(`SignUpForm.form_error_messages.${errors.cif}`)}
                      </div>
                    ) : null}
                  </div>
                  <Button
                    id="submit"
                    type="submit"
                    size="L"
                    text={t('SignUpForm.button_label')}
                  />
                </Form>
              );
            }}
          </Formik>
          {error && (
            <p className="error-field">{t(`SignUpForm.error_message`)}</p>
          )}
        </>
      )}
    </div>
  );
};

LandingRegisterForm.defaultProps = {
  t: (t) => t,
};

export default styled(LandingRegisterForm)`
  padding: 16px 0 16px 0;
  width: 100%;
  max-width: 350px;
  background: var(--white);
  color: var(--grey800);
  font-size: ${gridSizes('M')};
  border-bottom: 1px solid var(--grey300);

  .title {
    font-weight: ${weights('medium')};
    font-size: ${gridSizes('L')};
    margin: 0;
    color: var(--grey800);

    &.error {
      color: var(--red-cherry-text);
    }
    &.success {
      color: #4ca202;
    }
  }

  .form {
    margin: 12px 0 0;
    div input {
      border: 1px solid var(--grey200);
      border-radius: 4px;
      margin: 4px 0;
      color: var(--grey800);
    }
    div select {
      border: 1px solid var(--grey200);
      border-radius: 4px;
      margin: 4px 0;
    }
  }
  .error-input {
    input,
    select {
      border: 1px solid var(--red-cherry-text) !important;
    }
  }

  .error-field {
    color: var(--red-cherry-text);
    padding-top: ${gridSizes('XS')};
    padding-bottom: ${gridSizes('XS')};
    font-size: ${fontSizes('S')};
  }

  .error-msg {
    font-size: 14px;
    color: var(--red-cherry-text);
    margin: ${gridSizes('S')} 0 0;
  }
  button {
    margin: ${gridSizes('S')} 0 0;
    width: 100%;
    height: 32px !important;
    font-size: 14px !important;
  }
  a {
    display: block;
    font-size: 14px;
    font-weight: ${weights('bold')};
    color: var(--grey700);
    text-decoration: none;
    margin: ${gridSizes('M')} 0 0;
    text-align: center;
    &:hover {
      text-decoration: underline;
    }
  }
`;
